import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useAutoAnimate } from '@formkit/auto-animate/react'
import {
    useViewToggle,
    ViewType,
} from '../../../../stores/useViewAsToggle/useViewAsToggle'
import {
    UsageCostResponseBody,
    UsageCostRequestParams,
    useAnalyticsApi,
} from '../../../../hooks'
import { ErrorState, MultiChart, TableLoadingOverlay } from '../../../common'
import { UsageAndCostChartContainer } from '../../UsageAndCostChartContainer/UsageAndCostChartContainer'
import { UsageAndCostTableContainer } from '../../UsageAndCostTableContainer/UsageAndCostTableContainer'
import { ViewAsToggle } from '../../../molecules/ViewAsToggle/ViewAsToggle'
import { ContentPlaceholder } from '@/components/molecules'
import { InfoText } from '@/components/atoms'
import classNames from 'classnames'
import { useTotalUsns } from '@/features/Analytics/hooks/useTotalUsns'

type UsageAndCostReportProps = React.PropsWithChildren & {
    filters: UsageCostRequestParams
    dataSets: string[]
    isOpen?: boolean
    calculationMethodOffset?: number
}

export const UsageAndCostReport: React.FC<UsageAndCostReportProps> = (
    props
) => {
    const { dataSets, filters, isOpen = false, calculationMethodOffset } = props
    const [animationParent] = useAutoAnimate()
    const { view, setView } = useViewToggle('chart')
    const { t } = useTranslation('translations')

    const toggleContainerClassName = classNames(
        'flex items-center gap-4 p-2 -m-2'
    )

    return (
        <div
            className="grid w-full grid-cols-[100%] grid-rows-[auto_1fr] gap-6 p-6"
            ref={animationParent}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <div
                        className={classNames({
                            'transition-all duration-300 ease-out': isOpen,
                        })}
                        style={{
                            width: `${String(calculationMethodOffset)}px`,
                        }}
                    />
                    <InfoText
                        text={t(
                            'common.calculation_info.production_month.label'
                        )}
                        icon="info"
                        tooltipContent={t(
                            'common.calculation_info.production_month.tooltip'
                        )}
                    />
                </div>
                <div
                    className={toggleContainerClassName}
                    data-walkthrough="view-as-toggle"
                >
                    <ViewAsToggle
                        activeView={view}
                        onViewChange={setView}
                        data-testid="view-toggle-button"
                    />
                </div>
            </div>
            <UsageAndCostReportView
                dataSets={dataSets}
                filters={filters}
                activeView={view}
            />
        </div>
    )
}

const UsageAndCostReportView: React.FC<{
    filters: UsageCostRequestParams
    dataSets: string[]
    activeView: ViewType
}> = ({ filters, dataSets, activeView }) => {
    const { t } = useTranslation('translations')
    const { useUsageCostQuery } = useAnalyticsApi()
    const { data, isSuccess, isError, isFetched, isFetching, isLoading } =
        useUsageCostQuery(filters)

    const {
        totalUsns,
        isLoading: isUsnsLoading,
        isFetching: isUsnsFetching,
        isFetched: isUsnsFetched,
    } = useTotalUsns({
        nodeIds: filters['nodeIds[]'],
        commodityTypeId: filters.commodityTypeId,
        providerIds: filters['providerIds[]'],
        contractIds: filters['contractIds[]'],
        panIds: filters['panIds[]'],
        facilityIds: filters['facilityIds[]'],
        tagIds: filters['tagIds[]'],
    })

    const renderContent = ({ data }: { data?: UsageCostResponseBody }) => {
        const emptyData: UsageCostResponseBody = {
            totalItems: 0,
            totals: {
                cost: { unit: '$' },
                demand: { unit: 'KWH' },
                usage: { unit: 'KWH' },
            },
            items: [],
        }

        if (activeView === 'chart') {
            if (!data) {
                return <MultiChart data={emptyData.items} />
            }

            return (
                <UsageAndCostChartContainer
                    showSummaryDropdown
                    data={data}
                    dataSets={dataSets}
                    filters={filters}
                    totalUsns={totalUsns}
                />
            )
        }

        return (
            <UsageAndCostTableContainer
                data={data || emptyData}
                dataSets={dataSets}
                filters={filters}
            />
        )
    }

    const loadingMessage =
        activeView === 'chart'
            ? t('analytics.common.report_chart_loading')
            : t('analytics.common.report_table_loading')

    const emptyStateDescription =
        dataSets.length === 1
            ? t('analytics.common.report_empty_state.description_no_data_sets')
            : t('analytics.common.report_empty_state.description')

    if (isSuccess) {
        if (!data.items.length || !dataSets.length) {
            return (
                <ContentPlaceholder
                    title={t('analytics.common.report_empty_state.title')}
                    description={emptyStateDescription}
                    illustrationSrc="/assets/empty-state-illustration.svg"
                    name="empty-state"
                />
            )
        }
    }

    if (isError) {
        return <ErrorState description={t('analytics.common.report_error')} />
    }

    return (
        <div className="relative">
            {renderContent({ data: data })}
            <TableLoadingOverlay
                show={
                    isLoading ||
                    isFetching ||
                    !isFetched ||
                    isUsnsLoading ||
                    isUsnsFetching ||
                    !isUsnsFetched
                }
                title={loadingMessage}
            />
        </div>
    )
}
